import React, { useContext, useState } from "react";
import { Switch } from "react-router-dom";
import ChainOfCustodyContext from "./ChainOfCustodyContext";
import AppContext from "AppContext";
import PrivateRoute from "components/PrivateRoute";
import List from "./List";
import AddWizard from "./AddWizard";
import CocRequest from "./CocRequest";
import { useDispatch, useSelector } from "react-redux";
import { notifyError, notifySuccess } from "redux/actions/Notifications";
import { hasPermission } from "utils/session";
import { useRouteMatch, useHistory, Redirect } from "react-router-dom";

function ChainOfCustody({ fileCaseFilter }) {
    const { user } = useSelector((state) => state.Login);
    const dispatch = useDispatch();
    const history = useHistory();
    const { path } = useRouteMatch();

    const [addFormState, setAddFormState] = useState({
        client: undefined,
        requestType: undefined,
    });

    const setStatus = async (id, status, callback) => {
        try {
            const response = await CocRequests.setStatusAsync(id, status);
            callback(response.data);
            dispatch(notifySuccess("Status changed."));
        } catch (error) {
            dispatch(notifyError(error.response?.data?.message || error.message || "Unexpected error."));
        }
    };

    const {
        api: {
            ChainOfCustody: { CocRequests, Evidences, EvidenceDocs, EvidenceActivity, EvidenceAnalysis, EvidenceApprovals },
            General: { Storage },
            Management: { Customers },
            Settings: {
                ChainOfCustody: { RequestTypes, EvidenceTypes },
            },
        },
    } = useContext(AppContext);

    const isCocAdmin = hasPermission(user, "chain-of-custody.admin");
    const hasCocPermission = (permission) => isCocAdmin || hasPermission(user, permission);

    const canAdd = hasCocPermission("chain-of-custody.requests.add");
    const canEdit = hasCocPermission("chain-of-custody.requests.edit");
    const canView = canEdit || hasCocPermission("chain-of-custody.requests.view");
    const canAccess = canAdd || canView || hasCocPermission("chain-of-custody.access");

    const onCancel = () => {
        history.push(path);
    };

    return (
        <ChainOfCustodyContext.Provider
            value={{
                fileCaseFilter,
                CocRequests,
                Evidences,
                EvidenceDocs,
                EvidenceActivity,
                EvidenceAnalysis,
                EvidenceApprovals,
                Storage,
                Customers,
                RequestTypes,
                EvidenceTypes,
                addFormState,
                setAddFormState,
                setStatus,
                hasCocPermission,
                permissions: {
                    admin: isCocAdmin,
                    requests: {
                        add: canAdd,
                        edit: canEdit,
                        view: canView,
                        access: canAccess,
                        evidences: {
                            add: hasCocPermission("chain-of-custody.requests.evidences.add"),
                            delete: hasCocPermission("chain-of-custody.requests.evidences.delete"),
                            approve: hasCocPermission("chain-of-custody.requests.evidences.approve"),
                            revokeApproval: hasCocPermission("chain-of-custody.requests.evidences.revoke-approval"),
                            status: {
                                reject: hasCocPermission("chain-of-custody.requests.evidences.status.reject"),
                                complete: hasCocPermission("chain-of-custody.requests.evidences.status.complete"),
                                open: hasCocPermission("chain-of-custody.requests.evidences.status.open"),
                                inprogress: hasCocPermission("chain-of-custody.requests.evidences.status.inprogress"),
                            },
                            docs: {
                                view: hasCocPermission("chain-of-custody.requests.evidences.docs.view"),
                                add: hasCocPermission("chain-of-custody.requests.evidences.docs.add"),
                                open: hasCocPermission("chain-of-custody.requests.evidences.docs.open"),
                                delete: hasCocPermission("chain-of-custody.requests.evidences.docs.delete"),
                                markReceived: hasCocPermission("chain-of-custody.requests.evidences.docs.mark-received"),
                                unmarkReceived: hasCocPermission("chain-of-custody.requests.evidences.docs.unmark-received"),
                            },
                            activity: {
                                view: hasCocPermission("chain-of-custody.requests.evidences.activity.view"),
                                add: hasCocPermission("chain-of-custody.requests.evidences.activity.add"),
                            },
                            analysis: {
                                view: hasCocPermission("chain-of-custody.requests.evidences.analysis.view"),
                                add: hasCocPermission("chain-of-custody.requests.evidences.analysis.add"),
                                edit: hasCocPermission("chain-of-custody.requests.evidences.analysis.edit"),
                                delete: hasCocPermission("chain-of-custody.requests.evidences.analysis.delete"),
                            },
                            approvals: {
                                view: hasCocPermission("chain-of-custody.requests.evidences.approvals.view"),
                            },
                        },
                    },
                },
            }}
        >
            <Switch>
                {canAccess && <PrivateRoute exact path={`${path}`} component={List} />}
                {canAdd && <PrivateRoute exact path={`${path}/add/wizard`} component={AddWizard} />}
                {canAdd && <Redirect exact from={`${path}/add/wizard/:id`} to={`${path}/:id`} />}
                {canView && (
                    <PrivateRoute
                        exact
                        path={`${path}/:id`}
                        render={(props) => <CocRequest {...props} mode="view" onCancel={onCancel} />}
                    />
                )}
                {canEdit && (
                    <PrivateRoute
                        exact
                        path={`${path}/:id/edit`}
                        render={(props) => <CocRequest {...props} mode="edit" onCancel={onCancel} />}
                    />
                )}
            </Switch>
        </ChainOfCustodyContext.Provider>
    );
}

export default ChainOfCustody;
